// API client para apps/bookings/ (Django backend, NABU-71).
//
// Endpoints admin (Bearer JWT):
//   GET  /api/v1/admin/bookings-1to1/profiles
//   POST /api/v1/admin/bookings-1to1/profiles
//   GET  /api/v1/admin/bookings-1to1/services?profile_id=
//   POST /api/v1/admin/bookings-1to1/services
//   GET  /api/v1/admin/bookings-1to1/availability?profile_id=
//   POST /api/v1/admin/bookings-1to1/availability
//   GET  /api/v1/admin/bookings-1to1/?year=&month=&status=
//   GET  /api/v1/admin/bookings-1to1/<id>
//   POST /api/v1/admin/bookings-1to1/<id>/complete
//   POST /api/v1/admin/bookings-1to1/<id>/cancel
//
// Naming nota: el path es `/admin/bookings-1to1/` para no chocar con el
// app Django legacy `/admin/bookings/` (trip-bookings de Calaita).

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  AvailabilityRule,
  AvailabilityRuleCreateIn,
  Booking,
  BookingProfile,
  BookingProfileCreateIn,
  BookingService,
  BookingServiceCreateIn,
  BookingsListFilters,
} from './types';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/bookings-1to1';

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('org_id') || '';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Org-Id'] = orgId;
  return headers;
}

export class BookingsApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'BookingsApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = (body as { detail?: string }).detail || `HTTP ${res.status}`;
    throw new BookingsApiError(
      typeof detail === 'string' ? detail : JSON.stringify(detail),
      res.status,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export const bookingsApi = {
  // Profiles
  listProfiles: (): Promise<BookingProfile[]> => request(`${BASE}/profiles`),
  createProfile: (data: BookingProfileCreateIn): Promise<BookingProfile> =>
    request(`${BASE}/profiles`, { method: 'POST', body: JSON.stringify(data) }),

  // Services
  listServices: (profileId?: number): Promise<BookingService[]> => {
    const qs = profileId ? `?profile_id=${profileId}` : '';
    return request(`${BASE}/services${qs}`);
  },
  createService: (data: BookingServiceCreateIn): Promise<BookingService> =>
    request(`${BASE}/services`, { method: 'POST', body: JSON.stringify(data) }),

  // Availability
  listAvailability: (profileId: number): Promise<AvailabilityRule[]> =>
    request(`${BASE}/availability?profile_id=${profileId}`),
  createAvailability: (data: AvailabilityRuleCreateIn): Promise<AvailabilityRule> =>
    request(`${BASE}/availability`, { method: 'POST', body: JSON.stringify(data) }),

  // Bookings
  list: (filters: BookingsListFilters = {}): Promise<Booking[]> => {
    const p = new URLSearchParams();
    if (filters.year !== undefined) p.set('year', String(filters.year));
    if (filters.month !== undefined) p.set('month', String(filters.month));
    if (filters.status) p.set('status', filters.status);
    const qs = p.toString();
    return request(`${BASE}/${qs ? `?${qs}` : ''}`);
  },
  get: (id: number): Promise<Booking> => request(`${BASE}/${id}`),
  markCompleted: (id: number): Promise<Booking> =>
    request(`${BASE}/${id}/complete`, { method: 'POST', body: '{}' }),
  cancel: (id: number, reason = ''): Promise<Booking> =>
    request(`${BASE}/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};
